<template>
    <!-- Header -->
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ t('userGroups.title') }}</h4>
        <Button :icon="viewMode === 'table' ? 'fa-solid fa-grip' : 'fa-solid fa-table'" @click="toggleView" outlined />
    </div>

    <div class="pb-4">
        <!-- Card View -->
        <UserGroupCardView
            v-if="viewMode === 'card'"
            :user-groups="userGroups"
            :loading="loading"
            :search="cardSearch"
            :status-filter="cardStatusFilter"
            :first="cardFirst"
            :rows="cardPageSize"
            :total-records="cardMetadata?.filtered || 0"
            :notifications="notifications"
            @search="onCardSearch"
            @status-change="onCardStatusChange"
            @page="onCardPage"
            @create="handleCreate"
            @edit="handleEdit"
            @delete="handleDelete"
            @show-users="handleShowUsers"
            @show-notifications="handleShowNotifications"
        />

        <!-- Table View -->
        <UserGroupTableView
            v-else
            :user-groups="userGroups"
            :loading="loading"
            :filters="tableFilters"
            :sort-field="tableSortField"
            :sort-order="tableSortOrder"
            :first="tableFirst"
            :rows="tablePageSize"
            :total-records="tableMetadata?.total || 0"
            :filtered-records="tableMetadata?.filtered || 0"
            :notifications="notifications"
            @page="onTablePage"
            @filter="onTableFilter"
            @sort="onTableSort"
            @clear-filters="clearTableFilters"
            @create="handleCreate"
            @edit="handleEdit"
            @delete="handleDelete"
            @show-users="handleShowUsers"
            @show-notifications="handleShowNotifications"
        />
    </div>

    <!-- Modals -->
    <UserGroupModal v-if="groupModal.isOpen.value" :visible="groupModal.isOpen.value" :mode="groupModal.mode.value" :data="groupModal.data.value" :initialTab="modalInitialTab" @close="groupModal.close" @saved="onGroupSaved" />

    <UsersDialog :visible="usersDialogVisible" :title="usersDialogTitle" :users="selectedGroupUsers" :loading="loadingUsers" @close="usersDialogVisible = false" @manage-users="openEditDialogOnUsersTab" />

    <NotificationsDialog
        :visible="notificationsDialogVisible"
        :title="notificationsDialogTitle"
        :notifications="selectedGroupNotifications"
        :loading="loadingNotifications"
        @close="notificationsDialogVisible = false"
        @manage-notifications="openEditDialogOnNotificationsTab"
    />
</template>

<script setup lang="ts">
import UserGroupModal from '@/components/modal/UserGroup.vue';
import NotificationsDialog from '@/components/modal/UserGroupNotifications.vue';
import UsersDialog from '@/components/modal/UserGroupUsers.vue';
import UserGroupCardView from '@/components/userGroups/UserGroupCardView.vue';
import UserGroupTableView from '@/components/userGroups/UserGroupTableView.vue';
import { useModal } from '@/composables/useModal';
import { useUserGroupsPage } from '@/composables/useUserGroupsPage';
import type { User } from '@/lib/types/user';
import type { UserGroup, UserGroupNotification } from '@/lib/types/userGroup';
import { getInitialViewMode, ViewModeKey } from '@/utils/datatable';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast();

// Modal
const groupModal = useModal<UserGroup>();
const modalInitialTab = ref<string>('general');

// Page logic
const {
    // State
    viewMode,
    loading,
    userGroups,
    notifications,

    // Table
    tableFilters,
    tableSortField,
    tableSortOrder,
    tableFirst,
    tablePageSize,
    tableMetadata,

    // Card
    cardSearch,
    cardStatusFilter,
    cardFirst,
    cardPageSize,
    cardMetadata,

    // Methods
    toggleView,
    fetchData,
    initialize,
    onTablePage,
    onTableFilter,
    onTableSort,
    clearTableFilters,
    onCardPage,
    filterCards,

    // Helper methods
    loadGroupUsers,
    loadGroupNotifications
} = useUserGroupsPage(getInitialViewMode(ViewModeKey.USER_GROUPS));

// Card view handlers with debouncing
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function onCardSearch(value: string) {
    cardSearch.value = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        filterCards();
    }, 500);
}

function onCardStatusChange(value: boolean | null) {
    cardStatusFilter.value = value;
    filterCards();
}

// Modal handlers
function handleCreate() {
    modalInitialTab.value = 'general';
    groupModal.open('create');
}

function handleEdit(group: UserGroup) {
    modalInitialTab.value = 'general';
    groupModal.open('edit', group);
}

function handleDelete(group: UserGroup) {
    modalInitialTab.value = 'general';
    groupModal.open('delete', group);
}

// Users Dialog
const usersDialogVisible = ref(false);
const selectedGroupUsers = ref<User[]>([]);
const loadingUsers = ref(false);
const usersDialogTitle = ref('');
const currentDialogGroup = ref<UserGroup | null>(null);

async function handleShowUsers(group: UserGroup) {
    currentDialogGroup.value = group;
    usersDialogTitle.value = group.name;
    usersDialogVisible.value = true;
    loadingUsers.value = true;

    try {
        selectedGroupUsers.value = await loadGroupUsers(group);
    } catch (error) {
        console.error('Error loading users:', error);
        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: t('userGroups.errorLoadingUsers'),
            life: 3000
        });
    } finally {
        loadingUsers.value = false;
    }
}

function openEditDialogOnUsersTab() {
    usersDialogVisible.value = false;
    modalInitialTab.value = 'users';
    if (currentDialogGroup.value) {
        groupModal.open('edit', currentDialogGroup.value);
    }
}

// Notifications Dialog
const notificationsDialogVisible = ref(false);
const loadingNotifications = ref(false);
const notificationsDialogTitle = ref('');

const selectedGroupNotifications = computed<Record<string, UserGroupNotification>>(() => {
    if (!currentDialogGroup.value) return {};
    return notifications.value[currentDialogGroup.value.id] || {};
});

async function handleShowNotifications(group: UserGroup) {
    currentDialogGroup.value = group;
    notificationsDialogTitle.value = group.name;
    notificationsDialogVisible.value = true;
    loadingNotifications.value = true;

    try {
        await loadGroupNotifications(group.id);
    } catch (error) {
        console.error('Error loading notifications:', error);
        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: t('userGroups.errorLoadingNotifications'),
            life: 3000
        });
    } finally {
        loadingNotifications.value = false;
    }
}

function openEditDialogOnNotificationsTab() {
    notificationsDialogVisible.value = false;
    modalInitialTab.value = 'notifications';
    if (currentDialogGroup.value) {
        groupModal.open('edit', currentDialogGroup.value);
    }
}

// Event handlers
async function onGroupSaved() {
    toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: groupModal.mode.value === 'create' ? t('userGroups.createSuccess') : groupModal.mode.value === 'edit' ? t('userGroups.updateSuccess') : t('userGroups.deleteSuccess'),
        life: 3000
    });
    await fetchData();
}

// Lifecycle
onMounted(async () => {
    await initialize();
});
</script>
